/**
 * ====================================================================
 * FIKO CAD ANGULAR DIMENSION
 * ====================================================================
 *
 * İki çizgi/kenar arasındaki açı ölçüsü.
 * CATIA/IronCAD tarzında profesyonel açı ölçümü.
 *
 * Özellikler:
 * - Arc (yay) çizgisi
 * - Referans çizgileri (opsiyonel)
 * - Açı değeri (derece/radyan/DMS)
 * - Merkez işareti
 * - Oklu veya oksuz yay
 *
 * @author FikoYT CAD Team
 * @version 1.0.0
 */

import * as THREE from 'three';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { FikoSahne } from '../../FikoSahne';
import {
    Dimension,
    DimensionType,
    DimensionResult,
    DimensionState,
    GeometryReference,
    ReferenceType,
    AngularDimensionOptions,
    ArrowStyle,
    DIMENSION_CONSTANTS,
    DimensionErrorType
} from './DimensionTypes';

/**
 * Angular Dimension - Açı ölçüsü
 */
export class AngularDimension extends Dimension {
    // Referanslar
    private refA: GeometryReference;
    private refB: GeometryReference;

    // Hesaplanmış değerler
    private center: THREE.Vector3 = new THREE.Vector3();
    private startAngle: number = 0;
    private endAngle: number = 0;
    private arcRadius: number = 10;

    // Çizim elemanları
    private arcLine: Line2 | null = null;
    private refLine1: Line2 | null = null;
    private refLine2: Line2 | null = null;
    private arrow1: THREE.Mesh | THREE.Line | null = null;
    private arrow2: THREE.Mesh | THREE.Line | null = null;
    private label: THREE.Sprite | null = null;
    private centerMark: THREE.Group | null = null;

    // Shared geometries for arrow styles
    private static _sharedArrowGeometry: THREE.BufferGeometry | null = null;
    private static _sharedOpenArrowGeometry: THREE.BufferGeometry | null = null;
    private static _sharedDotGeometry: THREE.BufferGeometry | null = null;
    private static _sharedTickGeometry: THREE.BufferGeometry | null = null;

    // Sahne referansı
    private scene: FikoSahne;

    // Normal vektörü (açının düzlemi için)
    private normal: THREE.Vector3 = new THREE.Vector3(0, 0, 1);

    // Yön vektörleri
    private dirA: THREE.Vector3 = new THREE.Vector3();
    private dirB: THREE.Vector3 = new THREE.Vector3();

    // 🔴 SABİT BOYUT SİSTEMİ - FikoOlcumCizgisi ile aynı pattern
    sabitBoyut = true;
    private hazirlaListenerEklendi: boolean = false;
    private kontrolDegistiEvent = () => this.kameraYonDegistiThrottled();

    // 🚀 PERFORMANS: Smooth scale interpolation için
    private lastKameraUpdateTime: number = 0;
    private kameraUpdateTimeout: number | null = null;
    private static readonly KAMERA_THROTTLE_MS: number = 100;
    private targetScaleAdjustment: number = 1;
    private currentScaleAdjustment: number = 1;
    private interpolationRAF: number | null = null;
    private isDisposed = false;

    constructor(
        refA: GeometryReference,
        refB: GeometryReference,
        options: AngularDimensionOptions,
        scene: FikoSahne
    ) {
        super(DimensionType.ANGULAR, options);

        this.refA = refA;
        this.refB = refB;
        this.scene = scene;

        // Yay yarıçapı
        this.arcRadius = options.arcRadius || 10;
    }

    /**
     * Dimension'ı oluştur
     */
    create(): DimensionResult {
        try {
            // Açı hesapla
            const calculated = this.calculateAngle();
            if (!calculated) {
                return {
                    success: false,
                    error: {
                        type: DimensionErrorType.PARALLEL_LINES,
                        message: 'Açı hesaplanamadı - çizgiler paralel olabilir'
                    }
                };
            }

            // Değeri kaydet
            this._value = this.calculateValue();

            // Validate
            if (!this.validate()) {
                return {
                    success: false,
                    error: {
                        type: DimensionErrorType.INVALID_REFERENCE,
                        message: 'Açı çok küçük veya geçersiz'
                    }
                };
            }

            // Geometriyi oluştur
            this.buildGeometry();

            // 🎯 FikoOlcumCizgisi ile uyumlu: Sahneye hazırla (event listener bağla)
            this.hazirla();

            return {
                success: true,
                dimension: this
            };
        } catch (error) {
            return {
                success: false,
                error: {
                    type: DimensionErrorType.INTERNAL_ERROR,
                    message: `Angular dimension oluşturma hatası: ${error}`
                }
            };
        }
    }

    /**
     * Dimension'ı güncelle
     * 🎯 FikoOlcumCizgisi ile uyumlu: Sadece yazı sabit boyut, oklar ve çizgiler normal
     */
    update(): void {
        // 🎯 FikoOlcumCizgisi ile uyumlu: Oklar sabit boyutta kalır
        // Scale güncelleme event listener üzerinden yapılır (kameraYonDegisti)
    }

    /**
     * ✅ Sahneye hazırlama - FikoOlcumCizgisi ile aynı pattern
     */
    hazirla(): void {
        if (!this.scene || !this.scene.anaGosterim || !this.scene.anaGosterim.aktifKamera) {
            return;
        }

        try {
            if (this.label && this.label.userData.originalScale) {
                this.kameraYonDegisti();
            }

            if (!this.hazirlaListenerEklendi && this.scene.anaGosterim.aktifKontrol) {
                this.scene.anaGosterim.aktifKontrol.addEventListener("change", this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) {
            // Hata durumunda sessizce devam et
        }
    }

    /**
     * 🚀 PERFORMANS: Throttled kamera event handler
     */
    private kameraYonDegistiThrottled(): void {
        const now = performance.now();

        if (this.kameraUpdateTimeout !== null) {
            return;
        }

        if (now - this.lastKameraUpdateTime < AngularDimension.KAMERA_THROTTLE_MS) {
            this.kameraUpdateTimeout = window.setTimeout(() => {
                this.kameraUpdateTimeout = null;
                this.lastKameraUpdateTime = performance.now();
                this.kameraYonDegisti();
            }, AngularDimension.KAMERA_THROTTLE_MS - (now - this.lastKameraUpdateTime));
            return;
        }

        this.lastKameraUpdateTime = now;
        this.kameraYonDegisti();
    }

    /**
     * 🔴 YAZININ BOYUTU SABİT - Çizgi normal kalır
     */
    private kameraYonDegisti(): void {
        if (!this.scene || !this.scene.anaGosterim || !this.scene.anaGosterim.aktifKamera) {
            return;
        }

        if (this.sabitBoyut && this.label && this.label.userData.originalScale) {
            const kamera = this.scene.anaGosterim.aktifKamera as any;
            const zoom = kamera?.zoom ?? 1;
            const factor = ((kamera?.top ?? 100) - (kamera?.bottom ?? -100)) / zoom;

            this.targetScaleAdjustment = factor * 0.02;
            this.startSmoothInterpolation();
        }
    }

    /**
     * 🎯 SMOOTH: Her frame interpolation
     */
    private startSmoothInterpolation(): void {
        if (this.interpolationRAF !== null) return;

        const interpolate = () => {
            if (!this.label || !this.label.userData.originalScale || !this.sabitBoyut) {
                this.interpolationRAF = null;
                return;
            }

            const lerpFactor = 0.2;
            const diff = this.targetScaleAdjustment - this.currentScaleAdjustment;

            if (Math.abs(diff) < 0.001) {
                this.currentScaleAdjustment = this.targetScaleAdjustment;
                this.interpolationRAF = null;
                return;
            }

            this.currentScaleAdjustment += diff * lerpFactor;

            const originalScale = this.label.userData.originalScale as THREE.Vector3;
            this.label.scale.set(
                originalScale.x * this.currentScaleAdjustment,
                originalScale.y * this.currentScaleAdjustment,
                1
            );

            this.interpolationRAF = requestAnimationFrame(interpolate);
        };

        this.interpolationRAF = requestAnimationFrame(interpolate);
    }

    /**
     * Validate
     */
    validate(): boolean {
        const angleDeg = this._value;
        return angleDeg >= DIMENSION_CONSTANTS.MIN_ANGLE && angleDeg <= 180;
    }

    /**
     * Açı hesapla
     */
    private calculateAngle(): boolean {
        // Referans tipine göre işlem
        if (this.refA.type === ReferenceType.EDGE && this.refB.type === ReferenceType.EDGE) {
            return this.calculateAngleFromEdges();
        } else if (this.refA.type === ReferenceType.POINT && this.refB.type === ReferenceType.POINT) {
            // Üç nokta ile açı (refA = kol1, refB = merkez olarak varsayılır)
            return this.calculateAngleFromPoints();
        }

        return false;
    }

    /**
     * İki kenardan açı hesapla
     */
    private calculateAngleFromEdges(): boolean {
        // Kenar yönleri
        if (!this.refA.direction || !this.refB.direction) {
            return false;
        }

        this.dirA.copy(this.refA.direction).normalize();
        this.dirB.copy(this.refB.direction).normalize();

        // Kesişim noktası (merkez)
        if (this.refA.point && this.refB.point) {
            // İki çizginin kesişimini bul
            const intersection = this.findLineIntersection(
                this.refA.point,
                this.dirA,
                this.refB.point,
                this.dirB
            );

            if (intersection) {
                this.center.copy(intersection);
            } else {
                // Kesişim yoksa orta noktayı kullan
                this.center.lerpVectors(this.refA.point, this.refB.point, 0.5);
            }
        }

        // Normal vektörü (açı düzlemi)
        this.normal.crossVectors(this.dirA, this.dirB).normalize();
        if (this.normal.lengthSq() < 0.0001) {
            // Paralel çizgiler
            return false;
        }

        // Açıları hesapla
        const angle = Math.acos(Math.max(-1, Math.min(1, this.dirA.dot(this.dirB))));
        this.startAngle = 0;
        this.endAngle = angle;

        return true;
    }

    /**
     * Noktalardan açı hesapla
     */
    private calculateAngleFromPoints(): boolean {
        if (!this.refA.point || !this.refB.point) {
            return false;
        }

        // refB'yi merkez olarak al (3 nokta gerekli, şimdilik 2 nokta ile)
        this.center.copy(this.refB.point);

        // refA'dan merkeze yön
        this.dirA.subVectors(this.refA.point, this.center).normalize();

        // Varsayılan ikinci yön (X ekseni)
        this.dirB.set(1, 0, 0);
        if (Math.abs(this.dirA.dot(this.dirB)) > 0.99) {
            this.dirB.set(0, 1, 0);
        }

        // Normal
        this.normal.crossVectors(this.dirA, this.dirB).normalize();

        // Açı
        this.startAngle = 0;
        this.endAngle = Math.PI / 4; // Varsayılan 45 derece

        return true;
    }

    /**
     * İki çizginin kesişim noktasını bul
     */
    private findLineIntersection(
        p1: THREE.Vector3,
        d1: THREE.Vector3,
        p2: THREE.Vector3,
        d2: THREE.Vector3
    ): THREE.Vector3 | null {
        // 3D'de iki çizginin en yakın noktalarını bul
        const w0 = new THREE.Vector3().subVectors(p1, p2);

        const a = d1.dot(d1);
        const b = d1.dot(d2);
        const c = d2.dot(d2);
        const d = d1.dot(w0);
        const e = d2.dot(w0);

        const denom = a * c - b * b;

        if (Math.abs(denom) < 0.0001) {
            // Paralel çizgiler
            return null;
        }

        const t = (b * e - c * d) / denom;
        const s = (a * e - b * d) / denom;

        const closest1 = p1.clone().addScaledVector(d1, t);
        const closest2 = p2.clone().addScaledVector(d2, s);

        // Orta nokta
        return closest1.lerp(closest2, 0.5);
    }

    /**
     * Değeri hesapla (derece cinsinden)
     */
    protected calculateValue(): number {
        const angleRad = this.endAngle - this.startAngle;
        return angleRad * (180 / Math.PI);
    }

    /**
     * Geometriyi oluştur
     */
    protected buildGeometry(): void {
        this.clearGeometry();

        const options = this._options as AngularDimensionOptions;
        const color = options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        const lineWidth = options.lineWidth || DIMENSION_CONSTANTS.DEFAULT_LINE_WIDTH;

        // 1. Arc (yay) çizgisi
        this.arcLine = this.createArc(color, lineWidth);
        this.arcLine.name = 'ArcLine';
        this.add(this.arcLine);

        // 2. Referans çizgileri (opsiyonel)
        if (options.showRefLines !== false) {
            this.refLine1 = this.createRefLine(this.dirA, color, lineWidth * 0.6);
            this.refLine1.name = 'RefLine1';
            this.add(this.refLine1);

            this.refLine2 = this.createRefLine(this.dirB, color, lineWidth * 0.6);
            this.refLine2.name = 'RefLine2';
            this.add(this.refLine2);
        }

        // 3. Oklar (yay uçlarında)
        const arrowSize = options.arrowSize || DIMENSION_CONSTANTS.DEFAULT_ARROW_SIZE;

        // Başlangıç ve bitiş noktaları
        const startPoint = this.getPointOnArc(this.startAngle);
        const endPoint = this.getPointOnArc(this.endAngle);

        // Ok yönleri (yay teğeti)
        const startTangent = this.getTangentOnArc(this.startAngle);
        const endTangent = this.getTangentOnArc(this.endAngle);

        this.arrow1 = this.createArrow(startPoint, startTangent.negate(), color, arrowSize);
        this.arrow1.name = 'Arrow1';
        this.add(this.arrow1);

        this.arrow2 = this.createArrow(endPoint, endTangent, color, arrowSize);
        this.arrow2.name = 'Arrow2';
        this.add(this.arrow2);

        // 4. Label
        const midAngle = (this.startAngle + this.endAngle) / 2;
        const labelRadius = this.arcRadius * 1.3;
        const labelPos = this.getPointOnArc(midAngle, labelRadius);

        const angleText = this.formatAngleValue(this._value);
        this.label = this.createLabel(angleText, labelPos);
        this.label.name = 'Label';
        this.add(this.label);

        // 5. Merkez işareti (opsiyonel)
        // this.centerMark = this.createCenterMark(color);
        // this.add(this.centerMark);
    }

    /**
     * Arc oluştur
     */
    private createArc(color: number, lineWidth: number): Line2 {
        const segments = 32;
        const positions: number[] = [];

        for (let i = 0; i <= segments; i++) {
            const t = i / segments;
            const angle = this.startAngle + t * (this.endAngle - this.startAngle);
            const point = this.getPointOnArc(angle);
            positions.push(point.x, point.y, point.z);
        }

        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color,
            linewidth: lineWidth,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            fog: false,
            worldUnits: false,
            side: THREE.DoubleSide,
            toneMapped: false
        });

        const line = new Line2(geometry, material);
        line.renderOrder = Infinity;
        line.frustumCulled = false;
        line.computeLineDistances();

        return line;
    }

    /**
     * Arc üzerinde nokta al
     */
    private getPointOnArc(angle: number, radius?: number): THREE.Vector3 {
        const r = radius || this.arcRadius;

        // Döndürme için quaternion
        const quaternion = new THREE.Quaternion();
        quaternion.setFromAxisAngle(this.normal, angle);

        // Başlangıç yönünü döndür
        const point = this.dirA.clone().multiplyScalar(r);
        point.applyQuaternion(quaternion);

        // Merkeze göre konumlandır
        point.add(this.center);

        return point;
    }

    /**
     * Arc üzerinde teğet al
     */
    private getTangentOnArc(angle: number): THREE.Vector3 {
        // Teğet = normal × radial
        const radial = this.getPointOnArc(angle).sub(this.center).normalize();
        const tangent = new THREE.Vector3().crossVectors(this.normal, radial).normalize();
        return tangent;
    }

    /**
     * Referans çizgisi oluştur
     */
    private createRefLine(direction: THREE.Vector3, color: number, lineWidth: number): Line2 {
        const length = this.arcRadius * 1.5;

        const start = this.center.clone();
        const end = this.center.clone().addScaledVector(direction, length);

        const geometry = new LineGeometry();
        geometry.setPositions([start.x, start.y, start.z, end.x, end.y, end.z]);

        const material = new LineMaterial({
            color,
            linewidth: lineWidth,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity: 0.6,
            fog: false,
            worldUnits: false,
            dashed: true,
            dashScale: 1,
            dashSize: 2,
            gapSize: 1,
            side: THREE.DoubleSide,
            toneMapped: false
        });

        const line = new Line2(geometry, material);
        line.renderOrder = Infinity;
        line.frustumCulled = false;
        line.computeLineDistances();

        return line;
    }

    /**
     * Ok oluştur - ArrowStyle enum desteği
     */
    private createArrow(
        position: THREE.Vector3,
        direction: THREE.Vector3,
        color: number,
        size: number
    ): THREE.Mesh | THREE.Line {
        const arrowStyle = this._options.arrowStyle || ArrowStyle.FILLED;

        // NONE style - görünmez placeholder
        if (arrowStyle === ArrowStyle.NONE) {
            const empty = new THREE.Mesh();
            empty.visible = false;
            return empty;
        }

        const material = new THREE.MeshBasicMaterial({
            color,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            side: THREE.DoubleSide,
            toneMapped: false
        });

        let geometry: THREE.BufferGeometry;

        switch (arrowStyle) {
            case ArrowStyle.OPEN:
                // Açık ok - V şeklinde (CATIA tarzı geniş ok)
                if (!AngularDimension._sharedOpenArrowGeometry) {
                    const shape = new THREE.Shape();
                    shape.moveTo(-0.35, -0.5);
                    shape.lineTo(0, 0.5);
                    shape.lineTo(0.35, -0.5);
                    AngularDimension._sharedOpenArrowGeometry = new THREE.ShapeGeometry(shape);
                    AngularDimension._sharedOpenArrowGeometry.rotateX(Math.PI / 2);
                }
                geometry = AngularDimension._sharedOpenArrowGeometry;
                break;

            case ArrowStyle.DOT:
                // Nokta (görünür boyutta)
                if (!AngularDimension._sharedDotGeometry) {
                    AngularDimension._sharedDotGeometry = new THREE.SphereGeometry(0.25, 12, 12);
                }
                geometry = AngularDimension._sharedDotGeometry;
                break;

            case ArrowStyle.TICK:
                // Çizgi (architectural tick) - 45 derece çapraz çizgi, kalın
                if (!AngularDimension._sharedTickGeometry) {
                    const tickShape = new THREE.Shape();
                    tickShape.moveTo(-0.35, -0.35);
                    tickShape.lineTo(0.35, 0.35);
                    tickShape.lineTo(0.42, 0.28);
                    tickShape.lineTo(-0.28, -0.42);
                    tickShape.closePath();
                    AngularDimension._sharedTickGeometry = new THREE.ShapeGeometry(tickShape);
                    AngularDimension._sharedTickGeometry.rotateX(Math.PI / 2);
                }
                geometry = AngularDimension._sharedTickGeometry;
                break;

            case ArrowStyle.FILLED:
            default:
                // Dolu ok (CAD standardı) - Geniş ve uzun profesyonel ok
                // 🎯 FikoOlcumCizgisi ile uyumlu: Pivot tabanında, ok ucu pozisyon noktasında
                if (!AngularDimension._sharedArrowGeometry) {
                    AngularDimension._sharedArrowGeometry = new THREE.ConeGeometry(0.3, 0.8, 12);
                    // Pivot noktasını cone'nin tabanına taşı (ok ucu pozisyonda olacak)
                    AngularDimension._sharedArrowGeometry.translate(0, -0.4, 0); // height/2 = 0.8/2 = 0.4
                }
                geometry = AngularDimension._sharedArrowGeometry;
                break;
        }

        const arrow = new THREE.Mesh(geometry, material);
        arrow.position.copy(position);
        arrow.scale.setScalar(size);

        // Yönlendir - Y ekseni yukarı varsayılan (FikoOlcumCizgisi ile uyumlu)
        const quaternion = new THREE.Quaternion();
        quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);

        arrow.renderOrder = Infinity;
        arrow.frustumCulled = false;

        return arrow;
    }

    /**
     * Label oluştur
     */
    private createLabel(text: string, position: THREE.Vector3): THREE.Sprite {
        const options = this._options;
        const fontSize = options.fontSize || DIMENSION_CONSTANTS.DEFAULT_FONT_SIZE;
        const textColor = options.textColor || '#000000';

        // Canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        ctx.font = `bold ${fontSize * 4}px Arial, sans-serif`;
        const metrics = ctx.measureText(text);
        const textWidth = metrics.width;
        const textHeight = fontSize * 4;

        const padding = 16;
        canvas.width = textWidth + padding * 2;
        canvas.height = textHeight + padding * 2;

        ctx.font = `bold ${fontSize * 4}px Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // Beyaz outline
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.lineWidth = 4;
        ctx.strokeText(text, canvas.width / 2, canvas.height / 2);

        // Siyah yazı
        ctx.fillStyle = textColor;
        ctx.shadowColor = 'rgba(255, 255, 255, 0.95)';
        ctx.shadowBlur = 3;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;
        ctx.fillText(text, canvas.width / 2, canvas.height / 2);

        // Texture
        const texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;

        // Sprite
        const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            sizeAttenuation: true,
            toneMapped: false
        });

        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.position.copy(position);
        // 🎯 Kompakt yazı scale'i - küçük ve okunabilir
        sprite.scale.set(canvas.width * 0.015, canvas.height * 0.015, 1);
        sprite.renderOrder = Infinity;
        sprite.frustumCulled = false;

        // 🎯 FikoOlcumCizgisi ile uyumlu: Original scale'i sakla (sabit boyut için)
        sprite.userData.originalScale = new THREE.Vector3().copy(sprite.scale);

        return sprite;
    }

    /**
     * Açı değerini formatla
     */
    private formatAngleValue(angleDeg: number): string {
        const options = this._options as AngularDimensionOptions;
        const format = options.angleFormat || 'degrees';

        switch (format) {
            case 'radians':
                return (angleDeg * Math.PI / 180).toFixed(3) + ' rad';
            case 'dms':
                return this.toDMS(angleDeg);
            default:
                return angleDeg.toFixed(1) + '°';
        }
    }

    /**
     * Derece-dakika-saniye formatı
     */
    private toDMS(degrees: number): string {
        const d = Math.floor(degrees);
        const mFloat = (degrees - d) * 60;
        const m = Math.floor(mFloat);
        const s = ((mFloat - m) * 60).toFixed(1);
        return `${d}° ${m}' ${s}"`;
    }

    /**
     * Görünümü güncelle
     */
    protected updateAppearance(): void {
        let color: number;

        switch (this._state) {
            case DimensionState.HOVERED:
                color = DIMENSION_CONSTANTS.COLORS.HOVERED;
                break;
            case DimensionState.SELECTED:
                color = DIMENSION_CONSTANTS.COLORS.SELECTED;
                break;
            default:
                color = this._options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        }

        [this.arcLine, this.refLine1, this.refLine2].forEach(line => {
            if (line) {
                (line.material as LineMaterial).color.setHex(color);
            }
        });

        [this.arrow1, this.arrow2].forEach(arrow => {
            if (arrow) {
                (arrow.material as THREE.MeshBasicMaterial).color.setHex(color);
            }
        });
    }

    /**
     * Geometriyi temizle
     */
    private clearGeometry(): void {
        [this.arcLine, this.refLine1, this.refLine2].forEach(line => {
            if (line) {
                this.remove(line);
                line.geometry.dispose();
                (line.material as LineMaterial).dispose();
            }
        });

        [this.arrow1, this.arrow2].forEach(arrow => {
            if (arrow) {
                this.remove(arrow);
                arrow.geometry.dispose();
                (arrow.material as THREE.Material).dispose();
            }
        });

        if (this.label) {
            this.remove(this.label);
            this.label.material.map?.dispose();
            this.label.material.dispose();
        }

        if (this.centerMark) {
            this.remove(this.centerMark);
        }

        this.arcLine = null;
        this.refLine1 = null;
        this.refLine2 = null;
        this.arrow1 = null;
        this.arrow2 = null;
        this.label = null;
        this.centerMark = null;
    }

    /**
     * Dispose - 🎯 FikoOlcumCizgisi ile uyumlu kaynak temizleme
     */
    override dispose(): void {
        if (this.isDisposed) return;
        this.isDisposed = true;

        if (this.kameraUpdateTimeout !== null) {
            clearTimeout(this.kameraUpdateTimeout);
            this.kameraUpdateTimeout = null;
        }

        if (this.interpolationRAF !== null) {
            cancelAnimationFrame(this.interpolationRAF);
            this.interpolationRAF = null;
        }

        if (this.scene?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.scene.anaGosterim.aktifKontrol.removeEventListener("change", this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }

        this.clearGeometry();
        super.dispose();
    }
}
